import React, { useState, useRef, useEffect } from 'react';
import { List, Plus, LogOut, Users, ChevronDown, BarChart3, FileCheck, Settings } from 'lucide-react';
import SplashBILogo from './SplashBILogo';
import NotificationBell from './NotificationBell';

/**
 * Application Header Component
 * Clean header with logo, notifications, and user profile
 * Tabs are rendered separately below via TabNavigation
 *
 * @param {Object} props
 * @param {string} props.view - Current view ('list', 'create', 'admin', 'analytics', 'review', 'thresholds')
 * @param {Function} props.onViewList - Handler for viewing quote list
 * @param {Function} props.onNewQuote - Handler for creating new quote
 * @param {Function} props.onViewAdmin - Handler for viewing admin panel
 * @param {Function} props.onViewAnalytics - Handler for viewing analytics dashboard
 * @param {Function} props.onViewReview - Handler for viewing approval review
 * @param {Function} props.onViewThresholds - Handler for viewing threshold settings
 * @param {Object} props.user - Current user object { name, email, role, avatar }
 * @param {Object} props.notifications - Notification data { notifications, unreadCount, markAsRead, markAllAsRead }
 * @param {Function} props.onNotificationClick - Handler for clicking on a notification
 */
const Header = ({
  view,
  onViewList,
  onNewQuote,
  onViewAdmin,
  onViewAnalytics,
  onViewReview,
  onViewThresholds,
  user,
  notifications = {},
  onNotificationClick
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem('authToken');
    const tenantId = 'a01a9fad-47e6-46a8-8654-68e2b39b8a33';
    const postLogoutRedirect = encodeURIComponent(window.location.origin);
    window.location.href = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutRedirect}`;
  };

  // Role display labels
  const roleLabels = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    ceo: 'CEO',
    cro: 'CRO',
    cfo: 'CFO',
    cto: 'CTO',
    manager: 'Manager',
    sales: 'Account Executive',
    presales: 'Presales',
    implementation: 'Implementation',
    other: 'Other'
  };

  // Use permissions from backend instead of hardcoded role checks
  // Normalize role to lowercase for comparison
  const userRole = user?.role?.toLowerCase();
  const canManageUsers = user?.permissions?.canManageUsers ||
    ['super_admin', 'admin'].includes(userRole);
  const canViewAnalytics = user?.permissions?.canViewAnalytics ||
    ['super_admin', 'admin', 'ceo', 'cro', 'cfo', 'cto', 'manager', 'presales', 'implementation', 'other'].includes(userRole);
  const canApproveQuotes = user?.permissions?.canApproveQuotes ||
    ['super_admin', 'admin', 'ceo', 'cro', 'cto', 'manager'].includes(userRole);

  return (
    <>
      {/* Main Header - Clean and minimal */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo Section */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <SplashBILogo className="h-[50px]" />
                <div className="border-l border-slate-300 pl-3 ml-1">
                  <p className="text-xl font-bold text-slate-700">Quote Configurator</p>
                </div>
              </div>
            </div>

            {/* Right Section - Notifications & User */}
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <NotificationBell
                notifications={notifications.notifications || []}
                unreadCount={notifications.unreadCount || 0}
                onMarkAsRead={notifications.markAsRead}
                onMarkAllAsRead={notifications.markAllAsRead}
                onNotificationClick={onNotificationClick}
              />

              {/* User Info with Dropdown */}
              {user && (
                <div className="relative ml-2" ref={dropdownRef}>
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="flex items-center gap-3 hover:bg-slate-100/50 rounded-xl px-3 py-2 transition-colors"
                  >
                    <div className="text-right hidden md:block">
                      <div className="text-sm font-semibold text-slate-800">{user.name}</div>
                      <div className="text-xs text-slate-500">{roleLabels[user.role?.toLowerCase()] || user.role}</div>
                    </div>
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold shadow-lg shadow-blue-200">
                      {user.avatar}
                    </div>
                    <ChevronDown size={16} className={`text-slate-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-64 bg-white/90 backdrop-blur-xl rounded-xl shadow-xl border border-slate-200/50 py-1 z-50">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-sm font-medium text-slate-800 truncate">{user.name}</p>
                        <p className="text-xs text-slate-500 truncate" title={user.email}>{user.email}</p>
                      </div>

                      {canManageUsers && (
                        <>
                          <button
                            onClick={() => {
                              setShowDropdown(false);
                              onViewAdmin?.();
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                          >
                            <Users size={16} />
                            User Management
                          </button>
                          <button
                            onClick={() => {
                              setShowDropdown(false);
                              onViewThresholds?.();
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                          >
                            <Settings size={16} />
                            Discount Thresholds
                          </button>
                        </>
                      )}

                      <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Glassmorphic Tab Navigation - Below Header */}
      <nav className="sticky top-[82px] z-40 bg-gradient-to-r from-slate-50/80 via-white/80 to-slate-50/80 backdrop-blur-xl border-b border-slate-200/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1 py-3">
            {/* Quotes Tab */}
            <button
              onClick={onViewList}
              className={`px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all ${
                view === 'list'
                  ? 'bg-white/90 text-blue-600 shadow-lg shadow-blue-100/50 border border-blue-100'
                  : 'text-slate-600 hover:bg-white/50 hover:text-slate-800'
              }`}
            >
              <List size={18} />
              Quotes
            </button>

            {/* New Quote Tab */}
            <button
              onClick={onNewQuote}
              className={`px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all ${
                view === 'create'
                  ? 'bg-white/90 text-blue-600 shadow-lg shadow-blue-100/50 border border-blue-100'
                  : 'text-slate-600 hover:bg-white/50 hover:text-slate-800'
              }`}
            >
              <Plus size={18} />
              New Quote
            </button>

            {/* Review & Analytics Tabs - Permission Based */}
            {(canApproveQuotes || canViewAnalytics) && (
              <div className="w-px h-6 bg-slate-300/50 mx-2" />
            )}

            {/* Review Tab - Only for approvers */}
            {canApproveQuotes && (
              <button
                onClick={onViewReview}
                className={`px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all ${
                  view === 'review'
                    ? 'bg-white/90 text-amber-600 shadow-lg shadow-amber-100/50 border border-amber-100'
                    : 'text-slate-600 hover:bg-white/50 hover:text-slate-800'
                }`}
              >
                <FileCheck size={18} />
                Review
              </button>
            )}

            {/* Analytics Tab - For anyone with analytics permission */}
            {canViewAnalytics && (
              <button
                onClick={onViewAnalytics}
                className={`px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all ${
                  view === 'analytics'
                    ? 'bg-white/90 text-purple-600 shadow-lg shadow-purple-100/50 border border-purple-100'
                    : 'text-slate-600 hover:bg-white/50 hover:text-slate-800'
                }`}
              >
                <BarChart3 size={18} />
                Analytics
              </button>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;
